/**
 * Redux action creators for Salesforce case data fetching and management.
 *
 * @module redux/sf/actions
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import moment from 'moment';
import {
    FETCH_REQUEST_SF,
    FETCH_FAILURE_SF,
    FETCH_ALL_CASE_ID,
    FETCH_CASE_DETAILS,
    FETCH_FAILURE_SF_CASEIDS,
    SF_RESET,
    FETCH_CASE_CALLER_SUCCESS,
    FETCH_CASE_HISTORY,
    SF_DATA_LOADED,
} from './sfTypes';
import config from '../../config';
import {hexToString} from '../../utils/hexToString';

/**
 * Represents an action creator function that returns an action object
 * for initiating a fetch request. This function dispatches an action
 * with a specific type used to indicate the start of a fetch operation.
 *
 * @function fetchRequest
 * @returns {Object} An action object containing a `type` property with the value `FETCH_REQUEST_SF`.
 */
export const fetchRequest = () => ({
    type: FETCH_REQUEST_SF,
});

/**
 * Action creator function for handling failure in fetching data.
 *
 * @function fetchFailure
 * @param {Object} data - The data object representing error details or additional context for the failure.
 * @returns {Object} Action object containing the type of the action and the payload with the provided data.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE_SF,
    payload: data,
});

/**
 * Creates an action object for the fetch failure of SF case IDs.
 *
 * This function is used to generate an action object that signals a failure
 * in fetching Salesforce (SF) case IDs. It takes input data as its payload
 * and assigns it to the action object returned.
 *
 * @param {any} data - The payload representing the failure information for the Salesforce case IDs.
 * @returns {Object} An action object containing the type `FETCH_FAILURE_SF_CASEIDS` and the provided payload.
 */
export const fetchFailureCaseIds = (data) => ({
    type: FETCH_FAILURE_SF_CASEIDS,
    payload: data,
});

/**
 * Action creator for successfully fetching all case IDs.
 *
 * This function constructs and returns an action object with a type
 * and payload. The `type` indicates the action type, and the `payload`
 * contains the data representing all the fetched case IDs.
 *
 * @param {Object} data - The data representing all the fetched case IDs.
 * @returns {Object} An action object with the type `FETCH_ALL_CASE_ID` and the provided payload.
 */
export const fetchAllCaseIdSuccess = (data) => ({
    type: FETCH_ALL_CASE_ID,
    payload: data,
});

/**
 * Asynchronous function to fetch all case IDs from the server and dispatch
 * relevant actions based on the API response.
 *
 * This function:
 * - Dispatches a fetch request action at the start.
 * - Makes an HTTP GET request to the configured Salesforce API endpoint
 *   to retrieve case IDs.
 * - On a successful API response, dispatches the success action along with the response data.
 * - On failure, dispatches a failure action with the error details.
 *
 * @function fetchAllCaseId
 * @returns {Function} A thunk function that performs the asynchronous operation
 * and dispatches actions as needed (to be used with Redux).
 */
export const fetchAllCaseId = () => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.SF_API_BASE_URL}/api/v1/case/ids`, {
        headers: {
            Authorization: config.getToken(),
        },
    })
        .then((res) => dispatch(fetchAllCaseIdSuccess(res)))
        .catch((error) => dispatch(fetchFailureCaseIds(error)));
};

/**
 * Action creator for successfully fetching case details.
 *
 * This function generates an action object that signifies the
 * successful fetching of case details, containing the fetched
 * data as its payload.
 *
 * @param {Object} data - The data representing the details of the case.
 * @returns {Object} An action object with `type` set to FETCH_CASE_DETAILS
 * and `data` as the payload.
 */
export const fetchCaseDetailsSuccess = (data) => ({
    type: FETCH_CASE_DETAILS,
    payload: data,
});

/**
 * Fetches case details for a given case number and dispatches actions based on the result.
 *
 * @param {string} caseNumber - The unique identifier of the case to fetch details for.
 * @returns {Function} A thunk function that performs the asynchronous request and dispatches actions.
 *
 * - Dispatches `fetchRequest` to indicate the start of the fetch process.
 * - Performs an HTTP GET request to the Salesforce API endpoint to retrieve case details.
 * - If the request is successful, dispatches `fetchCaseDetailsSuccess` with the response data.
 * - If the request fails, dispatches `fetchFailure` with the error object.
 */
export const fetchCaseDetails = (caseNumber) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios.get(`${config.SF_API_BASE_URL}/api/v1/case-details?caseNumber=${caseNumber}`, {
        headers: {
            Authorization: config.getToken(),
        },
    })
        .then((res) => dispatch(fetchCaseDetailsSuccess(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * An action creator function that generates an action object to indicate the successful
 * fetching of caller information. It includes the fetch result details as payload.
 *
 * @param {Object} payload - The data returned after successfully fetching caller information.
 * @returns {Object} An action object containing the action type and payload.
 */
export const fetchCallerSuccess = (payload) => ({
    type: FETCH_CASE_CALLER_SUCCESS,
    payload,
});

/**
 * Action creator function to fetch case history data.
 *
 * This function generates an action object with a specific type and payload,
 * intended for use within a Redux framework. The action type identifies the
 * purpose of the action, while the payload carries the case history data.
 *
 * @param {Object} data - The case history data to be dispatched as payload.
 * @returns {Object} An action object containing the type `FETCH_CASE_HISTORY` and the provided payload.
 */
export const fetchCaseHistory = (data) => ({
    type: FETCH_CASE_HISTORY,
    payload: data,
});

/**
 * fetchHistory is a Redux-thunk action creator for fetching case history data
 * based on the provided ID. It dispatches a fetch request, performs an HTTP
 * GET request to the configured endpoint for case history, and updates the state
 * with the retrieved data or an error.
 *
 * @param {string} id - The ID associated with the case history to fetch.
 * @returns {Function} A thunk function that handles the asynchronous fetch operation.
 */
export const fetchHistory = (id) => async (dispatch) => {
    dispatch(fetchRequest);
    await axios(
        `${config.SF_API_BASE_URL}/api/v1/casehistory/fetch-history?id=${id}`,
    )
        .then((res) => {
            dispatch(fetchCaseHistory(hexToString(res.data.caseHistory).records));
        })
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Fetches case details for a given caller by making an API request and processes the returned data.
 *
 * The function retrieves case data based on the caller's name provided in the payload. It calculates the
 * time passed between the case's creation and closure, formats the data for front-end usage, and updates
 * the application's state accordingly. If an error occurs during the API request, the error is dispatched
 * to handle the failure.
 *
 * @param {string} [payload='DANANI WRIGHT-WOOTEN'] - The caller's name used to fetch associated case details.
 * @returns {function} A thunk action that dispatches requests, handles responses, and manages state updates.
 */
export const fetchCaseCaller = (payload = 'DANANI WRIGHT-WOOTEN') => (dispatch) => {
    dispatch(fetchRequest);
    axios.get(`${config.SF_API_BASE_URL}/api/v1/case-search?callerName=${payload}`, {
        headers: {
            Authorization: config.getToken(),
        },
    })
        .then((res) => {
            const data = hexToString(res.data[0].attributes);
            const a = moment(data.CreatedDate);
            const b = moment(data.ClosedDate);
            const days = b.diff(a, 'days');
            const hours = b.diff(a, 'hours');
            const passedTime = `${days} ${days > 1 ? 'Days ' : 'Day '}${hours > 0 ? hours : ''}${hours > 0 ? (hours > 1 ? ' Hours' : ' Hour') : ''} passed`;
            const payload = {
                caseId: data.Id,
                name: data.Caller_Name_Formula_DT__c,
                caseNumber: data.CaseNumber,
                caseRecordType: data.Case_Record_Type_Formula__c,
                openedDate: moment(data.CreatedDate).format('DD MM YYYY, h:mm A'),
                passedTime,
                recentCase: data.Recent_Case__c,
                recentCaseDate: moment(data.Recent_Case_Date__c).format(
                    'DD MM YYYY, h:mm A',
                ),
            };
            dispatch(fetchHistory(data.Id));
            dispatch(fetchCallerSuccess(payload));
        })
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Action creator function for resetting a specific feature or state within the application.
 * Returns an action object with the type property set to `SF_RESET`.
 *
 * @function sfReset
 * @returns {Object} The action object containing the type of `SF_RESET`.
 */
export const sfReset = () => ({
    type: SF_RESET,
});

/**
 * Action creator for setting the loaded state of data.
 * This function generates an action object that indicates the data
 * has been successfully loaded into the application state.
 *
 * @function
 * @param {Object} data - The data to be loaded.
 * @returns {Object} An action object containing the type of action and the loaded data as payload.
 */
export const setDataLoaded = (data) => ({
    type: SF_DATA_LOADED,
    payload: data,
});
